import { Button, ButtonGroup } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import { ThresholdsColumns } from 'features/inventory/warehouses/thresholds';
import { EnhancedCard } from 'components/enhanced/card/EnhancedCard';
import { EnhancedDataGrid } from 'components/enhanced/data-grid';
import { EnhancedTabs, TabItem } from 'components/enhanced/tabs/EnhancedTabs';
import { IsSuperAdmin, IsSuperAdminGuard } from 'shared/permissions/IsSuperAdmin';
import { useIsBottomFromTenantAdmin } from 'shared/permissions/useIsBottomFromTenantAdmin';
import ConfirmationButtonDialog from 'components/ui/feedback/ConfirmationButtonDelete';
import { stockQuantityService } from 'features/inventory/stock/api/stock-quantity.service';
import TenantPage from 'layouts/TenantPage';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { locationService, productService } from 'shared/services';
import { loadingActions } from 'store';
import { useAppDispatch, useAppSelector } from 'store/store';
import toast from 'shared/utils/toast';
import ProductPricesCard from '../components/card/ProductPricesCard';
import UpdateSuperAdminSapIdDialog from '../components/dialogs/UpdateSuperAdminSapIdDialog';
import { ProductForm } from '../components/form/ProductForm';
import CostAnalysisTab from '../components/tabs/CostAnalysisTab';
import WarehouseLocationTab from '../components/tabs/WarehouseLocationTab';
import { IProduct } from '../types';
import { productWarehouseService } from 'features/inventory/warehouses/api/product-warehouse.service';
import { warehouseService } from 'features/inventory/warehouses/api/warehouse.service';
import { StockQuantitiesColumns } from 'features/inventory/stock/stock-quantities';

const downloadableFields = ['ifu', 'ce_ivd', 'safety_document', 'technical_file'];

export const EditProduct = () => {
  const { t } = useTranslation();
  const translationPrefix = 'pages.product.edit';

  const thresholdsRefresh = useRef(null);

  const { productId } = useParams();
  const sap_active = useAppSelector((state) => state.auth.sap_active);
  const [product, setProduct] = useState<IProduct>();
  const [isSapIdDialogOpen, setIsSapIdDialogOpen] = useState(false);

  const isTenantAdminBottom = useIsBottomFromTenantAdmin();
  const isSuperAdminGuard = IsSuperAdminGuard();

  const dispatch = useAppDispatch();

  // Fetch the product
  const fetchProduct = () => {
    dispatch(loadingActions.startLoading());
    // Load current product
    productService.get(+productId).then((res) => {
      setProduct(undefined);
      setProduct(res);
      dispatch(loadingActions.stopLoading());
    });
  };

  useEffect(() => {
    fetchProduct();
  }, []);

  const onSubmit = async (values) => {
    dispatch(loadingActions.startLoading());
    productService.update(product.id, values).then(() => {
      toast.success(t(`${translationPrefix}.success`));
    });
    dispatch(loadingActions.stopLoading());
  };

  const handleRemoveLink = (id: number, pars: string) => {
    productService.removeCostLink(+productId, { model: pars, object_id: id }).then(() => {
      fetchProduct();
      toast.success(t(translationPrefix + '.price-origin-removed-success'));
    });
  };

  // When a warehouse is removed, remove it from the product
  const handleRemoveWarehouse = (entityId: number) => {
    warehouseService.removeProduct(entityId, product.id).then(() => {
      fetchProduct();
      toast.success(t(translationPrefix + '.warehouse-removed'));
    });
  };

  // When a location is removed, remove it from the product
  const handleRemoveLocation = (entityId: number) => {
    locationService.removeProduct(entityId, product.id).then(() => {
      fetchProduct();
      toast.success(t(translationPrefix + '.location-removed'));
    });
  };

  const handleThresholdsBulkDelete = (id?: number) => {
    productWarehouseService.bulkDeleteByProduct(+productId).then(() => {
      toast.success(t(`actions.successfully.deleted`));
      thresholdsRefresh.current();
    });
  };

  const openSapIdDialog = () => {
    setIsSapIdDialogOpen(true);
  };

  const closeSapIdDialog = () => {
    setIsSapIdDialogOpen(false);
  };

  const handleSuperAdminSapIdSubmit = async (values: { admin_sap_id: string }) => {
    dispatch(loadingActions.startLoading());
    try {
      await productService.updateSuperAdminSapId(+productId, values.admin_sap_id);
      toast.success(t(`${translationPrefix}.success`));
      closeSapIdDialog();
      fetchProduct();
    } finally {
      dispatch(loadingActions.stopLoading());
    }
  };

  const stockQuantitiesColumns: GridColDef[] = StockQuantitiesColumns();
  const thresholdColumns = ThresholdsColumns(true, null, thresholdsRefresh);

  const tabConfig: TabItem[] = product
    ? [
        {
          label: t(`global.base-info`),
          component: (
            <ProductForm product={product} onSubmit={onSubmit} disableEdit={!isTenantAdminBottom} />
          )
        },
        {
          label: t('menu.thresholds'),
          component: (
            <>
              <ProductPricesCard contractLines={product?.contract_lines} />
              <EnhancedCard
                title={t('menu.thresholds')}
                buttons={
                  <IsSuperAdmin>
                    <ConfirmationButtonDialog
                      handleDelete={handleThresholdsBulkDelete}
                      message={t(translationPrefix + '.superadmin-delete')}
                      iconButton={true}
                    />
                  </IsSuperAdmin>
                }>
                <EnhancedDataGrid
                  service={productWarehouseService}
                  columns={thresholdColumns}
                  refresh={thresholdsRefresh}
                  outerParams={{ product_id: productId }}
                />
              </EnhancedCard>
            </>
          )
        },
        {
          label: t('menu.stock'),
          component: (
            <EnhancedDataGrid
              service={stockQuantityService}
              getMethod={stockQuantityService.getStockQuantityWithLocation}
              columns={stockQuantitiesColumns}
              disableEdit={!isTenantAdminBottom}
              outerParams={{
                product__id: product?.id
              }}
            />
          )
        },
        {
          label: t('pages.product.tabs.warehouse_location'),
          component: (
            <WarehouseLocationTab
              product={product}
              fetchProduct={fetchProduct}
              handleRemoveLocation={handleRemoveLocation}
              handleRemoveWarehouse={handleRemoveWarehouse}
            />
          )
        },
        {
          label: t('global.costs'),
          visible: isSuperAdminGuard,
          variant: 'admin',
          component: <CostAnalysisTab product={product} handleRemoveLink={handleRemoveLink} />
        }
      ]
    : [];

  return (
    <TenantPage
      title={t(`${translationPrefix}.title`, { id: product?.id })}
      subtitle={t(`${translationPrefix}.subtitle`)}
      entityCode={product?.code + (sap_active ? ` (SAP ID ${product?.sap_id})` : '')}
      menuRight={
        <div className="flex justify-end items-center">
          {sap_active && (
            <ButtonGroup>
              <IsSuperAdmin>
                <Button size="small" onClick={openSapIdDialog}>
                  {t(`${translationPrefix}.sap_id_edit`)}
                </Button>
              </IsSuperAdmin>
            </ButtonGroup>
          )}
        </div>
      }>
      <EnhancedTabs
        tabs={tabConfig}
        variant="scrollable"
        documentConfig={{
          serviceName: 'product',
          entityId: product?.id,
          dictParams: { product: product?.id },
          customFolderName: `menu.products/${product?.code}`,
          folderDescription: `Descrizione: ${product?.description}; REF: ${product?.ref}; ${
            product?.sap_id ? `SAP ID: ${product?.sap_id}; ` : ''
          }`,
          disableEdit: !isTenantAdminBottom
        }}
        historyConfig={{ service: productService, entityId: product?.id }}
        attachmentsConfig={{
          fields: downloadableFields,
          entity: product,
          service: productService,
          visible: isTenantAdminBottom,
          translationPrefix: 'entities.product'
        }}
      />

      <UpdateSuperAdminSapIdDialog
        open={isSapIdDialogOpen}
        onClose={closeSapIdDialog}
        onSubmit={handleSuperAdminSapIdSubmit}
        sapId={product?.sap_id}
        oldSapId={product?.old_sap_id}
      />
    </TenantPage>
  );
};

export default EditProduct;
